import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';
import { CollectionAsset, CollectionEditState } from "../../../../../types/collections";

import Thumbnail from "../../../../../components/thumbnail";
import Reveal from "../../../../../components/animation";
import { FileService } from "../../../../../services/file.service";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';

import styles from './card.module.scss';
import Tooltip from "src/components/tooltip";
import { NavigateEntity } from "src/components/NavigateEntity";

interface AssetCardProps {
  asset: CollectionAsset;
  editable: boolean;
  collection : CollectionEditState;
  sectionIndex? : number;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: CollectionAsset) => void;

  // eslint-disable-next-line no-unused-vars
  onClick: (asset: CollectionAsset) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, editable, onRemove, onClick, collection, sectionIndex}) => {
  const assetItem = asset.asset;

  const fileType = useMemo(() => {
    return FileService.getFileType(_.get(assetItem, 'metadata'));
  }, [assetItem]);

  const fileSize = useMemo(() => {
    const size = _.get(assetItem, 'metadata.content_length');
    if(!size) return null;
    return FileService.formatSize(size);
  }, [assetItem]);

  const internal = !_.get(assetItem, 'settings.share', false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: asset.id,
    disabled: !editable,
    data: {
      asset: asset,
      type: 'asset'
    }
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
  }

  const handleClick = (e) => {
    e.preventDefault();
    if(!editable && onClick){
      onClick(asset);
    }
  }

  const getAssetUrl = (asset: CollectionAsset, section: number) => {
    const sectionIndex = section ? section : 0;
    let source = `collections/${collection.id}`;
    if (sectionIndex >= 0) {
      source = `${source}/sections/${sectionIndex}`;
    }
    return `/assets/${_.get(asset, 'asset.url')}?source=${source}`;
  };

  const assetUrl = getAssetUrl(asset, sectionIndex ?? 0);

  return (
    <div className={cx(styles.card, {
      [styles.editable]: editable,
      [styles.dragging]: isDragging
    })}
      style={style} ref={setNodeRef} {...listeners} {...attributes}
    >
    <NavigateEntity 
      path={assetUrl}
      onClick={handleClick}
      disabledClick={editable}
      >
      <div className={styles.icon}>
        <Thumbnail media={assetItem?.icon} defaultImage="/images/assets/default_asset.jpg" />
      </div>
      
      <div className={styles.overlay}></div>

      {internal && 
        <div className={styles.indicators}>
        <Tooltip content="Internal use only" placement="auto">
          <i className="paperflite-lock" />
        </Tooltip>
        </div>
      }

      <Reveal show={editable}>
        <button className={styles.remove} onClick={handleRemove}>
          <i className="fa-regular fa-circle-xmark"></i>
        </button>
      </Reveal>

      <div className={styles.cardContent}>
        <div className={styles.name}>
          {assetItem?.name || 'Untitled'}
        </div>
        <div className={styles.meta}>{fileType} {fileSize && `| ${fileSize}`}</div>
      </div>
      </NavigateEntity>
    </div>
  );
}


const PlaceholderAssetCard: React.FC<any> = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)}></div>
    </div>
  );
}

const DraggingAssetCard: React.FC<{ asset: CollectionAsset, overSection: boolean }> = ({ asset, overSection }) => {
  const assetItem = asset.asset;

  const fileType = useMemo(() => {
    return FileService.getFileType(assetItem?.metadata);
  }, [assetItem]);

  const fileSize = useMemo(() => {
    const size = _.get(assetItem, 'metadata.content_length');
    if(!size) return null;
    return FileService.formatSize(size);
  }, [assetItem]);

  return (
    <div className={cx(styles.card, styles.dragging, { [styles.overSection]: overSection })}>
      <div className={styles.icon}>
        <Thumbnail media={assetItem?.icon} defaultImage="/images/assets/default_asset.jpg" />
      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          {assetItem?.name || 'Untitled'}
        </div>
        <div className={styles.meta}>{fileType} {fileSize && `| ${fileSize}`}</div>
      </div>
      <div className={styles.message}>Add to section</div>
    </div>
  );
}

export { AssetCard, type AssetCardProps, PlaceholderAssetCard, DraggingAssetCard };